import { useCallback, useEffect, useRef } from 'react';

/**
 * A custom hook that creates a stable event callback reference.
 *
 * This hook is useful when you need to pass a callback to a child component
 * or an event handler that depends on changing values but should maintain
 * a stable reference to avoid unnecessary re-renders or re-subscriptions.
 *
 * The returned callback will always call the latest version of the provided
 * function (`fn`) without changing its reference, ensuring that closures
 * or dependencies inside the callback are always up-to-date.
 *
 * @template T - The type of the callback function.
 * @param fn - The callback function to be stabilized.
 * @returns A stable callback function that always calls the latest version of `fn`.
 */
export function useEventCallback<T extends (...args: any[]) => any>(fn: T): T {
  const ref = useRef(fn);

  useEffect(() => {
    ref.current = fn;
  });

  return useCallback(
    (...args: Parameters<T>): ReturnType<T> => ref.current(...args),
    []
  ) as T;
}
